import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  BASEMAP_ESRI_SATELLITE,
  BASEMAP_GOOGLE_HYBRID,
} from '@rsos/constants/basemapAddonNames';
import {
  BASEMAP_MENU_TYPE_ADDONS,
  BASEMAP_MENU_TYPE_BASEMAPS,
} from '@rsos/constants/basemapMenuTypes';
import useSelectedTheme from '@rsos/hooks/useSelectedTheme';
import { ESRI, GOOGLE } from '@rsos/maps/constants/mapProviders';
import BasemapTooltip from '../BasemapTooltip';
import {
  BasemapsAddonsBodyContainer,
  MenusContainer,
  MenuContainer,
  MenuIcon,
  MenuTitle,
  TooltipPosition,
} from './BasemapsAddonsBody.styles';

/**
 * Sub-component of BasemapMenu - body for basemaps/addons.
 * @param {Array} addons - List of addons
 * @param {Array} basemaps - List of basemaps
 * @param {String} menuType - Menu type - the value is either 'basemaps' or 'addons'
 * @param {Function} onHandleClick - Basemaps/addons click event
 */
const BasemapsAddonsBody = ({ addons, basemaps, menuType, onHandleClick }) => {
  const themeName = useSelectedTheme();

  const mapProvider = useSelector(state => state.sinatra.ui.mapProvider);

  const mapAddons = useSelector(state => state.sinatra.ui.mapAddons);

  const basemap = useSelector(state => state.sinatra.ui.basemap);

  const isBasemapVisible = useSelector(
    state => state.sinatra.ui.isBasemapVisible,
  );

  const isBasemapSelected = useCallback(
    selected => {
      const isSameMapProvider = mapProvider === selected.mapProvider;

      const isSameVisiblity = isBasemapVisible === selected.isBasemapVisible;

      const isSatellite =
        basemap === BASEMAP_ESRI_SATELLITE || basemap === BASEMAP_GOOGLE_HYBRID;

      const isSameBasemap = basemap === selected.basemaps[themeName];

      return (
        isSameMapProvider && isSameVisiblity && (isSatellite || isSameBasemap)
      );
    },
    [basemap, isBasemapVisible, mapProvider, themeName],
  );

  const isAddonSelected = useCallback(
    selectedAddon => {
      return mapAddons.includes(selectedAddon.name);
    },
    [mapAddons],
  );

  const isAddonDisabledForGoogle = useMemo(() => {
    return mapProvider === GOOGLE && menuType === BASEMAP_MENU_TYPE_ADDONS;
  }, [mapProvider, menuType]);

  const isAddonLockedForEsri = useMemo(() => {
    return (
      mapProvider === ESRI &&
      !isBasemapVisible &&
      menuType === BASEMAP_MENU_TYPE_ADDONS
    );
  }, [isBasemapVisible, mapProvider, menuType]);

  const handleBasemapClick = useCallback(
    ({ mapProvider, basemaps, isBasemapVisible }) => {
      onHandleClick(mapProvider, basemaps?.[themeName], isBasemapVisible);
    },
    [onHandleClick, themeName],
  );

  const handleAddonClick = useCallback(
    addonName => {
      mapProvider === ESRI && isBasemapVisible && onHandleClick(addonName);
    },
    [isBasemapVisible, mapProvider, onHandleClick],
  );

  return (
    <BasemapsAddonsBodyContainer
      data-name="basemaps-addons-body-container"
      isAddonDisabledForGoogle={isAddonDisabledForGoogle}
      isAddonLockedForEsri={isAddonLockedForEsri}
    >
      <MenusContainer data-name="basemaps-addons-menus-container">
        {basemaps?.length
          ? basemaps.map(map => {
              const {
                backgroundImage,
                basemaps,
                displayName,
                id,
                isBasemapVisible,
                mapProvider,
              } = map;

              const isSelected = isBasemapSelected(map);

              return (
                <MenuContainer
                  data-name="basemaps-menu-container"
                  key={id}
                  onClick={() =>
                    handleBasemapClick({
                      mapProvider,
                      basemaps,
                      isBasemapVisible,
                    })
                  }
                >
                  <MenuIcon
                    data-name="basemap-icon"
                    backgroundImage={backgroundImage}
                    isSelected={isSelected}
                  />
                  <MenuTitle data-name="basemap-title" isSelected={isSelected}>
                    {displayName}
                  </MenuTitle>
                </MenuContainer>
              );
            })
          : null}

        {addons?.length
          ? addons.map(addon => {
              return (
                <MenuContainer
                  data-name="addons-menu-container"
                  key={addon.id}
                  isAddonDisabledForGoogle={isAddonDisabledForGoogle}
                  isAddonLockedForEsri={isAddonLockedForEsri}
                  onClick={() => handleAddonClick(addon.name)}
                >
                  <MenuIcon
                    data-name="addon-icon"
                    backgroundImage={addon.backgroundImage}
                    isAddonDisabledForGoogle={isAddonDisabledForGoogle}
                    isAddonLockedForEsri={isAddonLockedForEsri}
                    isSelected={isAddonSelected(addon)}
                  />
                  <MenuTitle
                    data-name="addon-title"
                    isAddonDisabledForGoogle={isAddonDisabledForGoogle}
                    isAddonLockedForEsri={isAddonLockedForEsri}
                    isSelected={isAddonSelected(addon)}
                  >
                    {addon.displayName}
                  </MenuTitle>
                </MenuContainer>
              );
            })
          : null}
      </MenusContainer>

      {menuType === BASEMAP_MENU_TYPE_ADDONS ? (
        <TooltipPosition>
          <BasemapTooltip />
        </TooltipPosition>
      ) : null}
    </BasemapsAddonsBodyContainer>
  );
};

BasemapsAddonsBody.propTypes = {
  addons: PropTypes.arrayOf(
    PropTypes.shape({
      backgroundImage: PropTypes.string,
      displayName: PropTypes.string,
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  basemaps: PropTypes.arrayOf(
    PropTypes.shape({
      backgroundImage: PropTypes.string,
      basemaps: PropTypes.shape({
        default: PropTypes.string,
        light: PropTypes.string,
        dark: PropTypes.string,
      }),
      displayName: PropTypes.string,
      id: PropTypes.string,
      isBasemapVisible: PropTypes.bool,
    }),
  ),
  menuType: PropTypes.oneOf([
    BASEMAP_MENU_TYPE_ADDONS,
    BASEMAP_MENU_TYPE_BASEMAPS,
  ]),
  onHandleClick: PropTypes.func,
};

export default BasemapsAddonsBody;
